import { ENVELOPE_KEYS, ERROR_NODE, validateNode, validatePayload } from "@/platform/bridge/schema-rules"
import {
  BRIDGE_MESSAGE_DIRECTIONS,
  BRIDGE_MESSAGE_TYPES,
  BRIDGE_PROTOCOL_VERSION,
  type BridgeDataByType,
  type BridgeEnvelope,
  type BridgeError,
  type BridgeErrorCode,
  type BridgeMessageType,
  type BridgeResultMessageType,
} from "@/platform/bridge/types"

export type ParsedEnvelope = { ok: true; envelope: BridgeEnvelope } | { ok: false; error: BridgeError }

export type OutboundData<T extends BridgeMessageType> = T extends keyof BridgeDataByType ? BridgeDataByType[T] : Record<string, unknown>

const MESSAGE_TYPES: ReadonlySet<string> = new Set(BRIDGE_MESSAGE_TYPES)

const isPlainObject = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value)

export const isResultType = (type: string): type is BridgeResultMessageType => MESSAGE_TYPES.has(type) && type.endsWith(".result")

const isBridgeMessageType = (type: unknown): type is BridgeMessageType => typeof type === "string" && MESSAGE_TYPES.has(type)

export const createNotification = <T extends BridgeMessageType>(type: T, data?: OutboundData<T>): BridgeEnvelope =>
  (data === undefined ? { v: BRIDGE_PROTOCOL_VERSION, type } : { v: BRIDGE_PROTOCOL_VERSION, type, data }) as BridgeEnvelope

export const createRequest = <T extends BridgeMessageType>(type: T, id: string, data?: OutboundData<T>): BridgeEnvelope =>
  (data === undefined ? { v: BRIDGE_PROTOCOL_VERSION, type, id } : { v: BRIDGE_PROTOCOL_VERSION, type, id, data }) as BridgeEnvelope

export const createErrorEnvelope = (code: BridgeErrorCode, message: string): BridgeEnvelope<"error"> => ({
  v: BRIDGE_PROTOCOL_VERSION,
  type: "error",
  error: { code, message },
})

const rejected = (code: BridgeErrorCode, message: string): ParsedEnvelope => ({ ok: false, error: { code, message } })

export const parseInboundEnvelope = (raw: unknown): ParsedEnvelope => {
  let candidate = raw

  if (typeof candidate === "string") {
    try {
      candidate = JSON.parse(candidate)
    } catch {
      return rejected("invalid_data", "not JSON")
    }
  }

  if (!isPlainObject(candidate)) {
    return rejected("invalid_data", "not an envelope")
  }

  const { v, type, id, data, error } = candidate

  for (const key of Object.keys(candidate)) {
    if (!ENVELOPE_KEYS.has(key)) return rejected("invalid_data", `the envelope does not declare ${key}`)
  }

  if (v !== BRIDGE_PROTOCOL_VERSION) {
    return rejected("unsupported_version", `protocol ${String(v)} is not ${BRIDGE_PROTOCOL_VERSION}`)
  }

  if (!isBridgeMessageType(type)) {
    return rejected("unknown_type", `'${String(type)}' is not a bridge message type`)
  }

  if (BRIDGE_MESSAGE_DIRECTIONS[type] === "web_to_native") {
    return rejected("invalid_data", `'${type}' is web_to_native`)
  }

  if (id !== undefined && (typeof id !== "string" || id.length === 0)) {
    return rejected("invalid_data", `'${type}' id is not a non-empty string`)
  }

  if (data !== undefined && !isPlainObject(data)) {
    return rejected("invalid_data", `'${type}' data is ${data === null ? "null" : Array.isArray(data) ? "an array" : typeof data}, not an object`)
  }

  if (type === "session.probe" && (id === undefined || data === undefined)) return rejected("invalid_data", "session.probe needs id and data")

  if (isResultType(type)) {
    if (id === undefined) {
      return rejected("invalid_data", `'${type}' needs an id`)
    }
    if (data !== undefined && error !== undefined) {
      return rejected("invalid_data", `'${type}' carries data and error`)
    }
    if (data === undefined && error === undefined) {
      return rejected("invalid_data", `'${type}' carries neither data nor error`)
    }
  } else if (type === "error") {
    if (error === undefined) {
      return rejected("invalid_data", "'error' carries nothing to report")
    }
    if (data !== undefined) {
      return rejected("invalid_data", "'error' cannot carry data")
    }
  } else if (error !== undefined) {
    return rejected("invalid_data", `'${type}' cannot carry an error`)
  }

  if (error !== undefined) {
    const failure = validateNode(ERROR_NODE, error, `'${type}' error`)
    if (failure !== null) return rejected("invalid_data", failure)
  }

  if (data !== undefined) {
    const failure = validatePayload(type, data, `'${type}' data`)
    if (failure !== null) return rejected("invalid_data", failure)
  }

  return { ok: true, envelope: candidate as unknown as BridgeEnvelope }
}
